import React, { useState, useEffect } from 'react';
import { 
  DataGridPro, 
  GridToolbar,
  GridActionsCellItem,
  useGridApiRef,
  gridClasses,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter
} from '@mui/x-data-grid-pro';
import { 
  Box, 
  Paper, 
  Typography,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SettingsIcon from '@mui/icons-material/Settings';

// Sample hardcoded data
const sampleData = [
  { id: 1, firstName: 'John', lastName: 'Doe', status: 'Active', department: 'Engineering', role: 'Developer', email: 'john.doe@example.com', joinDate: new Date('2022-01-15'), salary: 85000, performance: 4.2 },
  { id: 2, firstName: 'Jane', lastName: 'Smith', status: 'Active', department: 'Marketing', role: 'Manager', email: 'jane.smith@example.com', joinDate: new Date('2021-06-22'), salary: 92000, performance: 4.5 },
  { id: 3, firstName: 'Robert', lastName: 'Johnson', status: 'On Leave', department: 'Finance', role: 'Analyst', email: 'robert.j@example.com', joinDate: new Date('2022-03-10'), salary: 78000, performance: 3.9 },
  { id: 4, firstName: 'Emily', lastName: 'Williams', status: 'Active', department: 'HR', role: 'Director', email: 'emily.w@example.com', joinDate: new Date('2020-11-05'), salary: 110000, performance: 4.8 },
  { id: 5, firstName: 'Michael', lastName: 'Brown', status: 'Inactive', department: 'Engineering', role: 'Senior Developer', email: 'michael.b@example.com', joinDate: new Date('2021-09-18'), salary: 95000, performance: 4.0 },
  { id: 6, firstName: 'Sarah', lastName: 'Miller', status: 'Active', department: 'Sales', role: 'Representative', email: 'sarah.m@example.com', joinDate: new Date('2022-02-28'), salary: 72000, performance: 3.7 },
  { id: 7, firstName: 'David', lastName: 'Anderson', status: 'Active', department: 'Engineering', role: 'QA Engineer', email: 'david.a@example.com', joinDate: new Date('2021-04-12'), salary: 80000, performance: 4.1 },
  { id: 8, firstName: 'Jennifer', lastName: 'Thomas', status: 'On Leave', department: 'Marketing', role: 'Specialist', email: 'jennifer.t@example.com', joinDate: new Date('2022-05-20'), salary: 76000, performance: 3.8 },
  { id: 9, firstName: 'James', lastName: 'Jackson', status: 'Active', department: 'Finance', role: 'Controller', email: 'james.j@example.com', joinDate: new Date('2020-08-15'), salary: 98000, performance: 4.3 },
  { id: 10, firstName: 'Lisa', lastName: 'White', status: 'Active', department: 'HR', role: 'Recruiter', email: 'lisa.w@example.com', joinDate: new Date('2021-10-30'), salary: 75000, performance: 4.0 },
  { id: 11, firstName: 'Daniel', lastName: 'Harris', status: 'Inactive', department: 'Engineering', role: 'Developer', email: 'daniel.h@example.com', joinDate: new Date('2021-07-19'), salary: 83000, performance: 3.5 },
  { id: 12, firstName: 'Michelle', lastName: 'Clark', status: 'Active', department: 'Sales', role: 'Manager', email: 'michelle.c@example.com', joinDate: new Date('2020-12-08'), salary: 90000, performance: 4.4 },
  { id: 13, firstName: 'Christopher', lastName: 'Lewis', status: 'Active', department: 'Engineering', role: 'Architect', email: 'chris.l@example.com', joinDate: new Date('2021-02-14'), salary: 105000, performance: 4.6 },
  { id: 14, firstName: 'Karen', lastName: 'Robinson', status: 'On Leave', department: 'Marketing', role: 'Coordinator', email: 'karen.r@example.com', joinDate: new Date('2022-04-05'), salary: 68000, performance: 3.6 },
  { id: 15, firstName: 'Matthew', lastName: 'Walker', status: 'Active', department: 'Finance', role: 'Accountant', email: 'matthew.w@example.com', joinDate: new Date('2021-08-22'), salary: 82000, performance: 4.2 },
  { id: 16, firstName: 'Laura', lastName: 'Hall', status: 'Active', department: 'HR', role: 'Specialist', email: 'laura.h@example.com', joinDate: new Date('2022-01-30'), salary: 74000, performance: 3.9 },
  { id: 17, firstName: 'Andrew', lastName: 'Allen', status: 'Inactive', department: 'Engineering', role: 'Team Lead', email: 'andrew.a@example.com', joinDate: new Date('2020-10-17'), salary: 97000, performance: 4.5 },
  { id: 18, firstName: 'Nancy', lastName: 'Young', status: 'Active', department: 'Sales', role: 'Executive', email: 'nancy.y@example.com', joinDate: new Date('2021-05-11'), salary: 88000, performance: 4.1 },
  { id: 19, firstName: 'Joshua', lastName: 'King', status: 'Active', department: 'Engineering', role: 'Developer', email: 'joshua.k@example.com', joinDate: new Date('2022-02-03'), salary: 84000, performance: 3.8 },
  { id: 20, firstName: 'Amanda', lastName: 'Scott', status: 'On Leave', department: 'Marketing', role: 'Analyst', email: 'amanda.s@example.com', joinDate: new Date('2021-11-25'), salary: 79000, performance: 4.0 }
];

// Sample columns configuration
const sampleColumns = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'firstName', headerName: 'First Name', width: 130, editable: true },
  { field: 'lastName', headerName: 'Last Name', width: 130, editable: true },
  { 
    field: 'status', 
    headerName: 'Status', 
    width: 120, 
    editable: true,
    type: 'singleSelect',
    valueOptions: ['Active', 'Inactive', 'On Leave'],
    renderCell: (params) => {
      let color = 'default';
      if (params.value === 'Active') color = 'success';
      if (params.value === 'Inactive') color = 'error';
      if (params.value === 'On Leave') color = 'warning';
      
      return <Chip label={params.value} color={color} size="small" variant="outlined" />;
    }
  },
  { field: 'department', headerName: 'Department', width: 150, editable: true },
  { field: 'role', headerName: 'Role', width: 150, editable: true },
  { field: 'email', headerName: 'Email', width: 200, editable: true },
  { field: 'joinDate', headerName: 'Join Date', width: 130, editable: true, type: 'date' },
  { 
    field: 'salary', 
    headerName: 'Salary', 
    type: 'number', 
    width: 120, 
    editable: true,
    valueFormatter: (params) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(params.value);
    }
  },
  { 
    field: 'performance', 
    headerName: 'Performance', 
    type: 'number',
    width: 140, 
    editable: true,
    valueFormatter: (params) => {
      return params.value.toFixed(1) + ' / 5.0';
    },
    renderCell: (params) => {
      let color = 'default';
      if (params.value >= 4.5) color = 'success';
      else if (params.value >= 4.0) color = 'info';
      else if (params.value >= 3.5) color = 'warning';
      else color = 'error';
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box 
            sx={{ 
              width: 50, 
              height: 8, 
              borderRadius: 4, 
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.3),
              mr: 1 
            }}
          >
            <Box 
              sx={{ 
                width: `${(params.value / 5) * 100}%`, 
                height: '100%', 
                borderRadius: 4, 
                bgcolor: (theme) => theme.palette[color].main 
              }} 
            />
          </Box>
          <Typography variant="caption">{params.value.toFixed(1)}</Typography>
        </Box>
      );
    }
  }
];

// Custom toolbar with additional features
function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      p: 1,
      borderBottom: '1px solid #eaeaea'
    }}>
      <Box>
        <GridToolbarColumnsButton sx={{ mr: 1 }} />
        <GridToolbarFilterButton sx={{ mr: 1 }} />
        <GridToolbarDensitySelector sx={{ mr: 1 }} />
        <GridToolbarExport 
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{ 
            fileName: 'enterprise-data-export',
            delimiter: ',',
            utf8WithBom: true
          }}
        />
      </Box>
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
}

// Main Enterprise Data Grid component
const EnterpriseDataGrid = ({ 
  title = 'Enterprise Dashboard',
  data = [], 
  columns = [], 
  height = 600,
  enableEditing = true,
  onRowUpdate,
  onRowDelete,
  loading = false,
  initialState = {},
  filterModel = { items: [] },
  paginationModel = { pageSize: 25, page: 0 },
  pageSizeOptions = [10, 25, 50, 100],
  checkboxSelection = true,
  showAnalyticsSummary = true
}) => {
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [rowModesModel, setRowModesModel] = useState({});
  const [gridData, setGridData] = useState(data);
  const [selectedRows, setSelectedRows] = useState([]);
  const [summaryData, setSummaryData] = useState({
    total: 0,
    selected: 0,
    filtered: 0
  });

  useEffect(() => {
    setGridData(data);
    // Initialize summary data with current data length
    updateSummaryData(data.length, 0, data.length);
  }, [data]);

  // Update summary data whenever selection or filtering changes
  useEffect(() => {
    if (apiRef.current) {
      // Get filtered rows count
      const filteredCount = apiRef.current.getFilteredRowsCount?.() || 
                           apiRef.current.getRowsCount?.() || 
                           data.length;
                           
      updateSummaryData(data.length, selectedRows.length, filteredCount);
    }
  }, [selectedRows, data, apiRef]);

  const updateSummaryData = (total, selected, filtered) => {
    setSummaryData({
      total,
      selected,
      filtered
    });
  };

  // Enhanced columns with action buttons
  const enhancedColumns = React.useMemo(() => {
    if (!enableEditing) return columns;
    
    return [
      ...columns,
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 120,
        getActions: (params) => [
          <GridActionsCellItem
            icon={
              <Tooltip title="Edit">
                <EditIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              </Tooltip>
            }
            label="Edit"
            onClick={() => handleEditClick(params.id)}
            sx={{ 
              '&:hover': { 
                backgroundColor: alpha(theme.palette.primary.main, 0.1) 
              }
            }}
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title="Delete">
                <DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
              </Tooltip>
            }
            label="Delete"
            onClick={() => handleDeleteClick(params.id)}
            sx={{ 
              '&:hover': { 
                backgroundColor: alpha(theme.palette.error.main, 0.1) 
              }
            }}
          />
        ],
      }
    ];
  }, [columns, enableEditing, theme]);

  // Handle editing
  const handleEditClick = (id) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: 'edit', fieldToFocus: columns[0].field }
    });
  };

  // Handle saving
  const handleSaveClick = (id) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: 'view' }
    });
    
    const updatedRow = gridData.find(row => row.id === id);
    
    if (onRowUpdate && updatedRow) {
      onRowUpdate(updatedRow);
    }
  };

  // Handle cancelling edit
  const handleCancelClick = (id) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: 'view', ignoreModifications: true }
    });
  };

  // Process row updates
  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow };
    
    setGridData(gridData.map(row => 
      row.id === newRow.id ? updatedRow : row
    ));
    
    return updatedRow;
  };

  // Handle deleting
  const handleDeleteClick = (id) => {
    if (window.confirm('Are you sure you want to delete this row?')) {
      setGridData(gridData.filter(row => row.id !== id));
      
      if (onRowDelete) {
        onRowDelete(id);
      }
    }
  };

  // Handle row selection
  const handleSelectionModelChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 0, 
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: theme.shadows[3],
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 2,
        borderBottom: '1px solid #eaeaea',
        backgroundColor: theme.palette.background.paper
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Download Data">
            <IconButton size="small">
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Analytics">
            <IconButton size="small">
              <AnalyticsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Settings">
            <IconButton size="small">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {showAnalyticsSummary && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          p: 2, 
          backgroundColor: alpha(theme.palette.primary.main, 0.04) 
        }}>
          <Chip 
            label={`Total: ${summaryData.total}`} 
            size="small" 
            sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
          />
          <Chip 
            label={`Selected: ${summaryData.selected}`} 
            size="small"
            sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}
          />
          <Chip 
            label={`Filtered: ${summaryData.filtered}`} 
            size="small"
            sx={{ backgroundColor: alpha(theme.palette.success.main, 0.1) }}
          />
        </Box>
      )}
      
      <Box sx={{ height, width: '100%' }}>
        <DataGridPro
          apiRef={apiRef}
          rows={gridData}
          columns={enhancedColumns}
          editMode="row"
          rowModesModel={rowModesModel}
          processRowUpdate={processRowUpdate}
          onRowEditStart={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onRowEditStop={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          slots={{
            toolbar: CustomToolbar,
          }}
          initialState={{
            ...initialState,
            pagination: {
              ...initialState.pagination,
              paginationModel,
            },
          }}
          filterMode="server"
          filterModel={filterModel}
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick={false}
          onRowSelectionModelChange={handleSelectionModelChange}
          loading={loading}
          getRowClassName={(params) => 
            params.indexRelativeToCurrentPage % 2 === 0 ? 
              `${gridClasses.row} even-row` : 
              `${gridClasses.row} odd-row`
          }
          sx={{
            boxShadow: 'none',
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.text.secondary,
              fontWeight: 600,
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: theme.palette.background.paper,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #f0f0f0',
            },
            '& .even-row': {
              backgroundColor: theme.palette.background.paper,
            },
            '& .odd-row': {
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell--editing': {
              backgroundColor: alpha(theme.palette.warning.light, 0.15),
              padding: '16px',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
            },
          }}
        />
      </Box>
    </Paper>
  );
};

// Demo component that uses the data grid with sample data
function EnterpriseDataGridDemo() {
  return (
    <EnterpriseDataGrid
      title="Employee Management Dashboard"
      data={sampleData}
      columns={sampleColumns}
      height={600}
      enableEditing={true}
      onRowUpdate={(updatedRow) => console.log('Row updated:', updatedRow)}
      onRowDelete={(id) => console.log('Row deleted:', id)}
      initialState={{
        sorting: {
          sortModel: [{ field: 'lastName', sort: 'asc' }],
        },
      }}
      showAnalyticsSummary={true}
    />
  );
}

// Export the demo component as the default export
export default EnterpriseDataGridDemo;